'use client';

import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import Link from 'next/link';

import { FlipsterLogo } from '@flipster/features/src/shared/logo';
import { useCheckSpotOnly } from '@flipster/features/src/shared/regulation';
import { FormattedMessage } from '@flipster/i18n';
import { languageLabels } from '@flipster/i18n/constants';
import { useSupportedLocale } from '@flipster/i18n/hooks';
import { useOverlay } from '@flipster/overlay';
import { IsomorphicLink } from '@flipster/react/design-utils/isomorphic-link';
import GlobalIcon from '@flipster/react/icon/generated/GlobalIcon';
import InformationCircleIcon from '@flipster/react/icon/generated/InformationCircleIcon';
import { useMounted } from '@flipster/react/misc/lifecycle/mount';
import { Styles, css } from '@flipster/styled-system/css';
import { circle, flex, grid, gridItem, hstack, stack } from '@flipster/styled-system/patterns';
import segment from '@flipster/utils/segment';
import { FooterEvent } from '@flipster/utils/segment/events/footer';

import { SnsItems } from '@web/app/_layout/footer/constants';
import { LanguageModal } from '@web/app/_layout/language-modal';
import { hideIfWebview } from '@web/components/HideIfWebview';

import { footerBunja } from './bunja';

interface Props {
  css?: Styles;
}

export const Footer = hideIfWebview(({ css: cssProps }: Props) => {
  const { footerMenusAtom } = useBunja(footerBunja);
  const locale = useSupportedLocale();

  const isMounted = useMounted();
  const overlay = useOverlay();
  const isSpotOnly = useCheckSpotOnly();

  const footerMenus = useAtomValue(footerMenusAtom);

  return (
    <footer className={css({ py: 40, px: 24, bgColor: 'background.default.level-1' }, cssProps)}>
      <div className={css({ maxW: 'desktopMaxWidth', mx: 'auto' })}>
        <nav className={grid({ columns: { base: 2, smallDesktop: 5 }, gap: 32 })}>
          <div
            className={gridItem({
              display: 'flex',
              flexDirection: 'column',
              colSpan: { base: 2, smallDesktop: 1 },
              gap: { base: 16, smallDesktop: 32 },
            })}
          >
            <Link href='/' aria-label='flipster'>
              <FlipsterLogo withType />
            </Link>
            <ul
              className={grid({
                gridTemplateColumns: { base: 'repeat(6, max-content)', smallDesktop: 'repeat(3, max-content)' },
                gap: 12,
              })}
            >
              {SnsItems.map((item) => (
                <li key={item.label}>
                  <IsomorphicLink
                    href={item.href}
                    aria-label={item.label}
                    newTab
                    className={circle({
                      size: 32,
                      display: 'flex',
                      bgColor: 'background.default.level-3',
                      color: 'inverse.white',
                      _hover: {
                        bgColor: 'background.default.level-4',
                      },
                    })}
                  >
                    <item.icon width={20} height={20} />
                  </IsomorphicLink>
                </li>
              ))}
            </ul>
            {isMounted && (
              <button
                type='button'
                onClick={() =>
                  overlay.open(({ isOpen, close, unmount }) => (
                    <LanguageModal isOpen={isOpen} onClose={close} onExit={unmount} />
                  ))
                }
                className={flex({
                  textStyle: 'Text/M/Regular',
                  align: 'center',
                  gap: 8,
                  color: 'content.default.level-5',
                  _hover: { color: 'inverse.white' },
                })}
              >
                <GlobalIcon size={24} />
                <span>{languageLabels[locale]}</span>
              </button>
            )}
          </div>
          {footerMenus
            .filter((menu) => !menu.isHide)
            .map((menu) => (
              <div key={menu.title}>
                <h3 className={css({ textStyle: 'Text/M/Strong', color: 'content.default.level-2', mb: 32, py: 6 })}>
                  {menu.title}
                </h3>
                <ul className={stack({ gap: 24 })}>
                  {menu.subMenus
                    .filter((sub) => !sub.isHide)
                    .map((sub) => (
                      <li key={`${menu.title}-${sub.title}`}>
                        <IsomorphicLink
                          href={sub.url || sub.externalUrl}
                          newTab={Boolean(sub.externalUrl)}
                          className={css({
                            textStyle: 'Text/M/Regular',
                            color: 'content.default.level-5',
                            _hover: { color: 'inverse.white' },
                          })}
                          onClick={() => {
                            segment.track(FooterEvent.FooterMenuClick, {
                              button_text: sub.title,
                              category: menu.title,
                            });
                          }}
                        >
                          {sub.title}
                        </IsomorphicLink>
                      </li>
                    ))}
                </ul>
              </div>
            ))}
        </nav>
        {isSpotOnly && (
          <div
            className={hstack({
              gap: 4,
              color: 'content.default.level-3',
              mt: 32,
              pt: 32,
              borderTopWidth: '1px',
              borderTopStyle: 'solid',
              borderColor: 'border.default.level-1',
            })}
          >
            <InformationCircleIcon size={16} />
            <p className={css({ textStyle: 'Text/S/Regular' })}>
              Flipster FZE is licensed by the Dubai Virtual Asset Regulatory Authority (VARA) under VASP Reference
              (VL/26/07/002) to provide (i) Exchange Services.
            </p>
          </div>
        )}
        <p className={css({ textStyle: 'Text/S/Regular', color: 'content.default.level-1', mt: 32 })}>
          Flipster © {new Date().getFullYear()} <FormattedMessage id='Autogen_common.r251126.s_Flipster_Corp' />
        </p>
      </div>
    </footer>
  );
});
